// =====================================================================
// REMISES COMMUNITYPLACE TAB — Audit/Stats des demandes remises par les COOPER
// Extrait de GestionTarifsDemandesPubliques.js (refactoring — comportement identique).
// =====================================================================
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { RefreshCw, CheckCircle, AlertTriangle, Recycle, Loader2, Activity } from 'lucide-react';
import { MOTIF_LABELS } from './GestionTarifsDemandesPubliques.constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RemisesCommunityplaceTab = () => {
  const [data, setData] = useState({ items: [], stats_30d: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/achats/admin/remises-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || { items: [], stats_30d: null });
    } catch (e) {
      toast.error("Échec chargement remises : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />Chargement…
      </div>
    );
  }

  const stats = data.stats_30d || {};

  return (
    <div className="space-y-4">
      {/* Policy banner */}
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-600 flex items-center justify-center">
              <Recycle className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-amber-900">Politique de remise en Communityplace</h3>
              <p className="text-sm text-amber-800 mt-1">
                Un COOPER&apos;S peut remettre une demande achetée si non traitée. Aucun remboursement du prix initial.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <div className="bg-white rounded-lg p-3 border border-amber-200">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">Délai de grâce</div>
                  <div className="text-xl font-bold text-slate-900 mt-1">{stats.grace_days ?? 7} jours</div>
                  <div className="text-xs text-slate-500 mt-0.5">sans pénalité après l&apos;achat</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-200">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">Pénalité de retard</div>
                  <div className="text-xl font-bold text-slate-900 mt-1">{stats.penalty_per_day ?? 15} crédits/j</div>
                  <div className="text-xs text-slate-500 mt-0.5">au-delà du délai de grâce</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-200">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">Quota anti-abus</div>
                  <div className="text-xl font-bold text-slate-900 mt-1">{stats.max_per_cooper_30d ?? 3} max</div>
                  <div className="text-xs text-slate-500 mt-0.5">par COOPER sur 30j glissants</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats du mois */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Recycle className="h-5 w-5 text-blue-700" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Remises ce mois</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums">{stats.count ?? 0}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-rose-700" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Crédits pénalité collectés</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums">{stats.penalty_credits_collected ?? 0}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle className="h-5 w-5 text-emerald-700" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Demandes rendues</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums">{(data.items || []).length}</div>
              <div className="text-[10px] text-slate-500">circulation Communityplace</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Log table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />Journal des remises
            </CardTitle>
            <CardDescription>
              {data.items?.length || 0} entrée(s) — du plus récent au plus ancien
            </CardDescription>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
            data-testid="refresh-remises-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
        </CardHeader>
        <CardContent>
          {(data.items || []).length === 0 ? (
            <div className="text-center py-10 border border-dashed border-slate-200 rounded-xl">
              <Recycle className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Aucune remise enregistrée pour l&apos;instant.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <th className="py-2 px-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Date</th>
                    <th className="py-2 px-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">COOPER</th>
                    <th className="py-2 px-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Demande</th>
                    <th className="py-2 px-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Motif</th>
                    <th className="py-2 px-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Délai</th>
                    <th className="py-2 px-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold text-right">Pénalité</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2.5 px-2 text-xs text-slate-700">{new Date(r.remitted_at).toLocaleString('fr-FR')}</td>
                      <td className="py-2.5 px-2 text-xs text-slate-700 font-medium">{r.user_email}</td>
                      <td className="py-2.5 px-2 text-xs font-mono text-slate-600">{r.demande_id?.slice(0, 8)}…</td>
                      <td className="py-2.5 px-2 text-xs">
                        <Badge variant="outline" className="bg-slate-100 text-slate-800 font-semibold">{MOTIF_LABELS[r.motif] || r.motif}</Badge>
                      </td>
                      <td className="py-2.5 px-2 text-xs">
                        <span className="text-slate-700">{r.days_since_purchase}j</span>
                        {r.days_over_grace > 0 && (
                          <span className="text-rose-700 ml-1">(+{r.days_over_grace}j retard)</span>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-xs text-right">
                        {r.penalty_credits > 0 ? (
                          <span className="font-bold text-rose-700">−{r.penalty_credits} cr.</span>
                        ) : (
                          <span className="text-emerald-700">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RemisesCommunityplaceTab;
